import classNames from 'styles/utils/classNames';
import { BookingDate } from './types';

interface Props {
  dates: BookingDate[];
  selectedDate: string;
  onDateChange: (localDate: string) => void;
  /** `lg` grows the day on desktop, as the club panel draws it; mobile is 48px everywhere. */
  size?: 'md' | 'lg';
  className?: string;
}

const SIZE_CLASSES = { md: 'size-12', lg: 'size-12 lg:size-14' } as const;

/**
 * The horizontal day picker above the slot grid.
 *
 * Scrolls rather than wraps — the design shows the next day peeking in at the
 * right edge, which is what tells the player there is more to scroll to.
 */
export default function DateStrip({
  dates,
  selectedDate,
  onDateChange,
  size = 'md',
  className,
}: Props) {
  return (
    <div
      className={classNames(
        'no-scrollbar -mx-4 flex gap-ds-sm overflow-x-auto px-4 sm:mx-0 sm:px-0',
        className,
      )}
    >
      {dates.map((date) => {
        const isSelected = date.localDate === selectedDate;

        return (
          <button
            key={date.localDate}
            type="button"
            onClick={() => onDateChange(date.localDate)}
            aria-pressed={isSelected}
            className={classNames(
              'relative flex shrink-0 flex-col items-center justify-center rounded-full text-center transition-colors',
              SIZE_CLASSES[size],
              isSelected
                ? 'bg-color-bg-lightmode-brand text-color-text-darkmode-primary'
                : 'bg-color-bg-lightmode-tertiary dark:bg-color-bg-darkmode-tertiary',
              !isSelected &&
                (date.isToday
                  ? 'text-color-text-brand'
                  : 'text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary'),
            )}
          >
            <span className="typography-product-text-card-mobile">{date.weekdayLabel}</span>
            <span className="typography-product-button-label-large">{date.dayLabel}</span>
            {date.hasDeals && !isSelected && (
              <span
                aria-label="Discounted times available"
                className="absolute bottom-0 right-[0.3125rem] size-2 rounded-full bg-color-bg-lightmode-brand"
              />
            )}
          </button>
        );
      })}
    </div>
  );
}
